import { cloneDeep } from 'lodash';
import { getFramework } from '@/framework';
import { isHttpUri } from '@/utils/file-kit';
import { getLayerRuntimeValue } from '@/utils/layer-kit';
import { getOrCreateScreenshotUrlCode, hydrateScreenshotUrlCode } from '@/utils/screenshot-url-kit';
import type { ILayerProps, IMarkupLayerProps } from '@/version-types';
import { getMarkupLabelLayoutKey, type IMarkupLabelLayout } from './label-layout';
import { prepareLabelLayout } from './markup-create';
import { normalizeMarkupLayerForRuntime } from './normalize-layer';

export const getMarkupScreenshotFingerprintData = (layer: ILayerProps) => {
    const data = cloneDeep(layer.data) as IMarkupLayerProps;
    delete data.screenshotUrl;
    delete data.__vipFuncType;
    delete data.__drawClick;
    delete data.__isQuickDraw;

    return {
        width: Math.floor(layer.width),
        height: Math.floor(layer.height),
        ...data,
    };
};

export const hydrateMarkupLayerScreenshotUrl = (layer: ILayerProps) => {
    const data = layer.data as IMarkupLayerProps;
    data.screenshotUrl = hydrateScreenshotUrlCode({
        fingerprintData: getMarkupScreenshotFingerprintData(layer),
        remoteUrl: data.screenshotUrl,
    });
    return layer;
};

export const initializeMarkupLayerForRuntime = (layer: ILayerProps) => {
    const isFirstInit = !layer.__runtime?.isInit;
    normalizeMarkupLayerForRuntime(layer);
    const data = layer.data as IMarkupLayerProps;
    if (isFirstInit && data.screenshotUrl && isHttpUri(data.screenshotUrl)) {
        hydrateMarkupLayerScreenshotUrl(layer);
    }
    return layer;
};

export async function getScreenshotUrlCode(identity: string, layer: ILayerProps) {
    const runtimeLayout = getLayerRuntimeValue<IMarkupLabelLayout>(layer, '__markupLabelLayout');
    if (!runtimeLayout || runtimeLayout.key !== getMarkupLabelLayoutKey(layer)) {
        await prepareLabelLayout(layer, identity);
    }

    if (layer.width <= 0 || layer.height <= 0) return '';

    return getOrCreateScreenshotUrlCode({
        fingerprintData: getMarkupScreenshotFingerprintData(layer),
        createDataUrl: async () => {
            const canvas = await getFramework(identity).service.behavior.get(layer)?.getLayerImageAsync?.(layer, false);
            return canvas?.toDataURL();
        },
    });
}

const timerMap = {};

export function updateScreenshotUrl(identity: string, id: string) {
    return new Promise((resolve) => {
        const layerTimer = timerMap[id];
        if (layerTimer) {
            clearTimeout(layerTimer);
        }
        (function (id) {
            timerMap[id] = setTimeout(() => {
                (async function (timerId) {
                    const framework = getFramework(identity);
                    let layer = framework.service.flatLayer.getLayer(id);
                    if (!framework.service.flatLayer.getLayer(id)) return;
                    const code = await getScreenshotUrlCode(identity, layer);
                    layer = framework.service.flatLayer.getLayer(id);
                    if (layer) {
                        (layer.data as IMarkupLayerProps).screenshotUrl = code;
                    }
                    if (timerId === timerMap[id]) {
                        resolve(layer);
                        delete timerMap[id];
                    }
                })(timerMap[id]);
            }, 33);
        })(id);
    });
}
